import React, { useState, JSX } from 'react';
import { useMount } from 'react-use';
import {
  ApiBooking,
  ApiBookingState,
  ApiNote,
  ApiNoteType,
  ApiPayment,
  ApiPaymentOption,
  ApiPaymentState,
  ApiTraveler
} from '@ibe/api';
import { Button, Col, Row } from 'reactstrap';
import LoadingOverlay from '../../UI/LoadingOverlay/LoadingOverlay';
import useMMBBookingService from '../../../../MMB/Hooks/useBookingService';
import Error from '../../UI/Messages/Error';
import useTranslation from '../../../Hooks/useTranslation';
import fallback from '../../../../_Translations/generated/booking-details-module.en.t.json';
import Keys from '../../../../_Translations/generated/booking-details-module.en.t.json.keys';
import { Price } from '../../UI/Price/Price';
import DateDisplay from '../../UI/DateDisplay/DateDisplay';
import Summary from '../../Cart/Summary/Summary';
import BookingDocuments from '../../BookingDocuments/BookingDocuments';
import CancelRequestModule from '../../CancelRequest/CancelRequestModule';

export interface BookingDetailsModuleProps {
  bookingId: string;
  hideCancelOption?: boolean;
}

const BookingDetailsModule = (props: BookingDetailsModuleProps): JSX.Element => {
  const mmbBookingService = useMMBBookingService();
  const { bookingId, hideCancelOption } = props;
  const { t } = useTranslation('booking-details-module', fallback);
  const [loading, setLoading] = useState<boolean>(false);
  const [booking, setBooking] = useState<ApiBooking | null>(null);
  const [hasError, setHasError] = useState<boolean>(false);
  const [cancelRequestOpen, setCancelRequestOpen] = useState<boolean>(false);

  useMount(async () => {
    try {
      setLoading(true);
      const b = await mmbBookingService.getBooking(bookingId, true);
      setBooking(b);
      setHasError(false);
    } catch (error) {
      console.error(error);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  });

  function updateCancelRequestOpen(newValue: boolean): void {
    setCancelRequestOpen(newValue && !hideCancelOption);
  }

  const paidAuthorizedPayment: ApiPaymentOption | undefined = booking?.payments.find(
    (p: ApiPayment) => p.state === ApiPaymentState.PAID || p.state === ApiPaymentState.AUTHORIZED
  )?.paymentOption;

  return (
    <div>
      <LoadingOverlay isLoading={loading}>
        {loading ? <div className="booking-details-module-loader"></div> : null}
        {hasError && <Error message="Error text" />}
        {booking && (
          <>
            <div className="mb-3 iso__bookingDetailsModule_header_container p-3">
              <Row className="mb-0">
                <Col xs="12" md="6" className="align-self-center">
                  <div className="iso__bookingDetailsModule_header_bold">
                    {t(Keys.bookingNumber)}: {booking.bookingNumber}
                  </div>
                </Col>
                <Col xs="12" md="6" className="align-self-center">
                  <div className="iso__bookingDetailsModule_header_bold">
                    {t(Keys.totalAmount)}: <Price price={booking.price} displayInline />
                  </div>
                </Col>
              </Row>
              <Row className="mt-0">
                <Col xs="12" md="6">
                  <div className="iso__bookingDetailsModule_header_regular">
                    {t(Keys.bookingState)}:{' '}
                    {t(`bookingStateValue.${booking.bookingState.toString()}`)}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs="12" className="offset-md-6">
                  <div className="iso__bookingDetailsModule_header_regular">
                    {t(Keys.arrivalDate)}: <DateDisplay date={booking.travelStartDate} />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs="12" md="6" className="order-1 order-md-0">
                  <div className="iso__bookingDetailsModule_header_regular">
                    {t(Keys.bookingDate)}: <DateDisplay date={booking.bookingDate} />
                  </div>
                </Col>
                <Col xs="12" md="6" className="order-0 order-md-1">
                  <div className="iso__bookingDetailsModule_header_regular">
                    {t(Keys.departureDate)}: <DateDisplay date={booking.travelEndDate} />
                  </div>
                </Col>
              </Row>
            </div>
            <div className="mb-3 mt-2">
              <Row>
                <Col xs="12">
                  <div className="iso__bookingDetailsModule_header_medium mb-2">
                    {t(Keys.paymentStatusHeader)}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs="12" md="6">
                  <div className="iso__bookingDetailsModule_header_regular">
                    {t(Keys.openPayment)}:{' '}
                    {booking.openAmount && <Price price={booking.openAmount} displayInline />}
                  </div>
                </Col>
                <Col xs="12" md="6">
                  <div className="iso__bookingDetailsModule_header_regular">
                    {t(Keys.dueDate)}:{' '}
                    {booking.paymentDueDate && <DateDisplay date={booking.paymentDueDate} />}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs="12" md="6" className="iso__bookingDetailsModule_header_regular">
                  {t(Keys.paidAmount)}:{' '}
                  {booking.payments && (
                    <Price
                      price={
                        booking.payments.find(
                          (p: ApiPayment) =>
                            p.state === ApiPaymentState.PAID ||
                            p.state === ApiPaymentState.AUTHORIZED
                        )?.amount
                      }
                      displayInline
                    />
                  )}
                </Col>
                <Col xs="12" md="6" className="iso__bookingDetailsModule_header_regular">
                  {t(Keys.paymentType)}:{' '}
                  {paidAuthorizedPayment && (
                    <span>{t(`paymentOptionValue.${paidAuthorizedPayment.toString()}`)}</span>
                  )}
                </Col>
              </Row>
              <Row>
                <Col xs="12" md="6" className="iso__bookingDetailsModule_header_regular">
                  {t(Keys.paymentDate)}:{' '}
                  {booking.payments && (
                    <span>
                      {booking.payments
                        .filter((p: ApiPayment) => p.state === ApiPaymentState.PAID)
                        .map((openPayment: ApiPayment) => (
                          <DateDisplay date={openPayment.paymentDate} />
                        ))}
                    </span>
                  )}
                </Col>
                <Col xs="12" md="6" className="iso__bookingDetailsModule_header_regular">
                  <span className="">{t(Keys.paymentStatus)}: </span>
                  {booking.paymentState && (
                    <span>{t(`paymentStateValue.${booking.paymentState}`)}</span>
                  )}
                </Col>
              </Row>
            </div>
            <div className="mb-3">
              <Row>
                <Col xs="12">
                  <div className="iso__bookingDetailsModule_header_medium mb-2">
                    {t(Keys.bookedService)}
                  </div>
                </Col>
              </Row>
              <Summary booking={booking} />
            </div>
            <div className="mb-3">
              <Row>
                <Col xs="12">
                  <div className="iso__bookingDetailsModule_header_medium mb-2">
                    {t(Keys.traveler)}
                  </div>
                </Col>
              </Row>
              {booking.travelers.map((traveler: ApiTraveler) => (
                <div key={`${traveler.firstName}-${traveler.id}`} className="border-bottom mb-4">
                  <Row>
                    <Col className="iso__bookingDetailsModule_header_regular">
                      {traveler.salutation} {traveler.lastName} {traveler.firstName}{' '}
                      {traveler.middleName}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col className="iso__bookingDetailsModule_header_regular">
                      {traveler.birthDate && <DateDisplay date={traveler.birthDate} />}
                      {traveler.communicationDetails.email
                        ? `, ${traveler.communicationDetails.email}`
                        : ''}
                      {traveler.documentNumber ? `, ${traveler.documentNumber}` : ''}
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
            <div className="mb-3">
              <BookingDocuments booking={booking} bookingId={bookingId} />
            </div>
            <div className="mb-3">
              <Row>
                <Col xs="12">
                  <div className="iso__bookingDetailsModule_header_medium mb-2">
                    {t(Keys.notes)}
                  </div>
                </Col>
              </Row>
              <Row>
                {booking.notes && booking.notes.length > 0 ? (
                  <>
                    {booking.notes
                      .filter((n: ApiNote) => n.type === ApiNoteType.INFO)
                      .map((note: ApiNote) => (
                        <Col xs="12" className="mb-2">
                          {note.text}
                        </Col>
                      ))}
                  </>
                ) : (
                  <Col xs="12">{t(Keys.noNotes)}</Col>
                )}
              </Row>
            </div>
            {booking?.bookingState !== ApiBookingState.CANCELLED && !hideCancelOption && (
              <div className="text-right">
                <Button
                  className="align-self-end"
                  color="primary"
                  onClick={(): void => {
                    setCancelRequestOpen(true);
                  }}
                >
                  <span className="px-2">{t(Keys.cancel)}</span>
                </Button>
              </div>
            )}
          </>
        )}
        {booking && (
          <CancelRequestModule
            isOpen={cancelRequestOpen}
            setIsOpen={updateCancelRequestOpen}
            bookingNumber={booking?.bookingNumber}
          />
        )}
      </LoadingOverlay>
    </div>
  );
};

export default BookingDetailsModule;
